import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { LOCAL_STORAGE, ROLES, ROUTES } from '../../../constants';
import DashboardContent from './DashboardContent';
import { HiringClientListing } from '../HiringClientListing';
import { DocumentListing } from '../DocumentListing';
import { ScoreCardReport } from '../ScoreCardReport';
import { Notification } from '../../shared';
import { EditProfile } from '../../shared';
import { DigitalSafetyLibraryCustomer } from '../DigitalSafetyLibraryCustomer';
import LegendPage from '../../shared/common/LegendPage';
import SafetyMeetingsCustomer from '../SafetyMeetingsCustomer/SafetyMeetingsCustomer';
import { AllNewsFeedCustomer } from '../NewsFeed/AllNewsFeedCustomer';
import { DigitalLaborLawPostersCustomer } from '../DigitalLaborLawPostersCustomer/DigitalLaborLawPostersCustomer';
import { FoldersView } from '../MyDocuments/FoldersView';
import { DocumentsView } from '../MyDocuments/DocumentsView';
import { getLocalStorageItem, popup } from '../../../utils';
import { updateUserVisitPageAPI } from '../../../utils/apiServices/userReportAPI';
import AccountReview from '../../Admin/AccountReview';
import AccountReviewPage from '../../Admin/AccountReview/AccountReviewPage';
import { ScorecardLandingPage } from '../ScoreCardReport/ScorecardLandingPage';

// eslint-disable-next-line no-undef
const ALLOW_HEARTBEAT = process.env.REACT_APP_PAGE_VISIT_HEARTBEAT !== 'false';
const ALLOW_HEARTBEAT_INTERVAL =
    // eslint-disable-next-line no-undef
    parseInt(process.env.REACT_APP_PAGE_VISIT_HEARTBEAT_INTERVAL_SEC) || 120;

const RenderDashboard = (selectedTab) => {
    switch (selectedTab) {
        case ROUTES.CUSTOMER.DASHBOARD:
            return <DashboardContent />;
        case ROUTES.CUSTOMER.DOCUMENTS:
            return <DocumentListing />;
        case ROUTES.CUSTOMER.SCORE_CARD_REPORT:
            return <ScoreCardReport />;
        case ROUTES.CUSTOMER.SCORE_CARD_LANDING_PAGE:
            return <ScorecardLandingPage />;
        case ROUTES.CUSTOMER.HIRING_CLIENTS:
            return <HiringClientListing />;
        case ROUTES.CUSTOMER.NOTIFICATION:
            return <Notification />;
        case ROUTES.CUSTOMER.EDIT_PROFILE:
            return <EditProfile />;
        case ROUTES.CUSTOMER.DIGITAL_SAFETY_LIBRARY:
            return <DigitalSafetyLibraryCustomer />;
        case ROUTES.CUSTOMER.LEGEND:
            return <LegendPage />;
        case ROUTES.CUSTOMER.SAFETY_MEETINGS:
            return <SafetyMeetingsCustomer />;
        case ROUTES.CUSTOMER.NEWS_FEEDS:
            return <AllNewsFeedCustomer />;
        case ROUTES.CUSTOMER.DIGITAL_LABOR_POSTERS:
            return <DigitalLaborLawPostersCustomer />;
        case ROUTES.CUSTOMER.MY_FOLDERS_VIEW:
            return <FoldersView />;
        case ROUTES.CUSTOMER.MY_DOCUMENTS_VIEW:
            return <DocumentsView />;
        case ROUTES.CUSTOMER.ACCOUNT_REVIEWS:
            return <AccountReview />;
        case ROUTES.CUSTOMER.ACCOUNT_REVIEW_PAGE:
            return <AccountReviewPage />;
        default:
            return <DashboardContent />;
    }
};

const ContentBox = ({ currentRoute, pathname, isImpersonated }) => {
    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const userId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const userRole = useSelector((state) => state.user.role);
    const visitCallRef = useRef(null);
    const intervalRef = useRef(null);

    const startHeartbeat = () => {
        if (intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            visitCallRef.current({ exit_time: moment.utc().toISOString() });
        }, ALLOW_HEARTBEAT_INTERVAL * 1000);
    };

    const stopHeartbeat = () => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
    };

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden' && visitCallRef.current) {
            // Tab hidden/minimized: exit + stop tracking
            visitCallRef.current({ exit_time: moment.utc().toISOString() });
            ALLOW_HEARTBEAT && stopHeartbeat();
        } else if (document.visibilityState === 'visible' && visitCallRef.current) {
            // Tab visible again: treat like new visit
            visitCallRef.current({ entry_time: moment.utc().toISOString() }); // backend sets entry_time
            ALLOW_HEARTBEAT && startHeartbeat();
        }
    };

    const sendVisitUpdate = async (data = {}) => {
        try {
            await updateUserVisitPageAPI({
                accountId,
                userId,
                path: pathname,
                data,
            });
        } catch (error) {
            popup('error', error?.message);
        }
    };

    useEffect(() => {
        const isRealUser = !!accountId && !!userId && !isImpersonated && userRole === ROLES.USER;
        if (isRealUser) {
            visitCallRef.current = sendVisitUpdate;
            // First Visit Log
            visitCallRef.current({ entry_time: moment.utc().toISOString() });
            ALLOW_HEARTBEAT && startHeartbeat();
            document.addEventListener('visibilitychange', handleVisibilityChange);
        }

        return () => {
            // Force flush on unmount
            if (isRealUser) {
                visitCallRef.current({
                    exit_time: moment.utc().toISOString(),
                });
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                ALLOW_HEARTBEAT && stopHeartbeat();
                visitCallRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, isImpersonated, accountId, userId, userRole]);

    return <div className='col-lg-10 ssc-main-content pb-3'>{RenderDashboard(currentRoute)}</div>;
};
ContentBox.propTypes = {
    currentRoute: PropTypes.string.isRequired,
};

export default ContentBox;
